import { toast } from "react-toastify"

import { REQ } from "constants/api/requests"
import { IResponse } from "constants/interfaces"

import useApi, { parseError } from "util/api"

import { SeoOverride, SeoOverrideGroup, SeoOverrideInput } from "./type"

class SeoService {
  /** Grouped by route — one entry per path, not one per language. */
  public async listOverrideGroups(): Promise<SeoOverrideGroup[] | null> {
    try {
      const { data } = await useApi.get<IResponse<{ items: SeoOverrideGroup[] }>>(REQ.ADMIN_DASHBOARD.SEO.OVERRIDES)
      return data?.response?.items ?? null
    } catch {
      toast.error("Failed to load SEO overrides")
      return null
    }
  }

  /** Every language of one route, which is what the editor opens. */
  public async listOverridesForPath(path: string): Promise<SeoOverride[] | null> {
    try {
      const { data } = await useApi.get<IResponse<{ items: SeoOverride[] }>>(REQ.ADMIN_DASHBOARD.SEO.OVERRIDES_BY_PATH, {
        params: { path }
      })
      return data?.response?.items ?? null
    } catch {
      toast.error("Failed to load this route")
      return null
    }
  }

  public async deleteOverridePath(path: string): Promise<boolean> {
    try {
      await useApi.delete(REQ.ADMIN_DASHBOARD.SEO.OVERRIDES_BY_PATH, { params: { path } })
      return true
    } catch (err: any) {
      parseError(err, "Failed to delete this route")
      return false
    }
  }

  public async createOverride(input: SeoOverrideInput): Promise<SeoOverride | null> {
    try {
      const { data } = await useApi.post<IResponse<SeoOverride>>(REQ.ADMIN_DASHBOARD.SEO.OVERRIDES, input)
      return data?.response ?? null
    } catch (err: any) {
      parseError(err, "Failed to create SEO override")
      return null
    }
  }

  public async updateOverride(id: string, patch: Partial<SeoOverrideInput>): Promise<SeoOverride | null> {
    try {
      const { data } = await useApi.patch<IResponse<SeoOverride>>(REQ.ADMIN_DASHBOARD.SEO.OVERRIDE_BY_ID(id), patch)
      return data?.response ?? null
    } catch (err: any) {
      parseError(err, "Failed to update SEO override")
      return null
    }
  }

  public async deleteOverride(id: string): Promise<boolean> {
    try {
      await useApi.delete(REQ.ADMIN_DASHBOARD.SEO.OVERRIDE_BY_ID(id))
      return true
    } catch (err: any) {
      parseError(err, "Failed to delete SEO override")
      return false
    }
  }
}

export const seoService = new SeoService()
